// Audio plumbing for Mode Suara (Gemini Live via the backend relay).
//
// ONE AudioContext serves both directions: the mic path downsamples whatever
// rate the hardware gives us (usually 44.1/48k) to both 16kHz PCM16 for
// Gemini voice and 24kHz PCM16 for OpenAI live transcription, and the
// playback path feeds 24kHz PCM16 into AudioBuffers created at their native
// 24000 rate (the context resamples on output). Capture uses a
// ScriptProcessorNode on purpose: deprecated, but it is the one path that
// works everywhere including iOS Safari without CRA asset plumbing for
// worklets, and a voice chat's 256ms latency budget is comfortable.

// Mobile echo cancellers need a few seconds of real speaker output before they
// converge. During that warm-up, sending the model its own voice makes Gemini
// think the user interrupted it, so replies stutter or stop even in a silent
// room. Keep capture running (so the browser's AEC can learn), but do not upload
// mic frames while Power AI is audibly speaking during the warm-up window.
// Normal full-duplex/barge-in automatically returns afterwards.
const ECHO_GUARD_WARMUP_MS = 20_000;
const ECHO_GUARD_TAIL_MS = 420;

export interface VoiceAudioHandlers {
  onChunk: (b64Pcm16k: string, b64Pcm24k: string) => void;
  onLevel?: (rms: number) => void;      // 0..1-ish mic level for the orb
}

function resamplePcm16(input: Float32Array, inputRate: number, targetRate: number): Int16Array {
  const ratio = inputRate / targetRate;
  const outLen = Math.floor(input.length / ratio);
  const out = new Int16Array(outLen);
  for (let i = 0; i < outLen; i++) {
    const start = Math.floor(i * ratio);
    const end = Math.min(input.length, Math.max(start + 1, Math.floor((i + 1) * ratio)));
    let acc = 0;
    for (let j = start; j < end; j++) acc += input[j];
    const sample = acc / Math.max(1, end - start);
    out[i] = Math.max(-32768, Math.min(32767, Math.round(sample * 32767)));
  }
  return out;
}

function pcm16ToBase64(samples: Int16Array): string {
  const bytes = new Uint8Array(samples.buffer);
  let bin = '';
  const STEP = 0x8000;
  for (let i = 0; i < bytes.length; i += STEP) {
    bin += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + STEP)));
  }
  return btoa(bin);
}

export class VoiceAudio {
  private ctx: AudioContext | null = null;
  private stream: MediaStream | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private processor: ScriptProcessorNode | null = null;
  // Playback queue bookkeeping: schedule each chunk right after the previous.
  private nextPlayTime = 0;
  private playing: AudioBufferSourceNode[] = [];
  private onPlaybackActivity: ((active: boolean) => void) | null = null;
  private activityTimer: ReturnType<typeof setTimeout> | null = null;
  private echoGuardStartedAt = 0;
  private playbackActive = false;
  private suppressCaptureUntil = 0;

  async start(handlers: VoiceAudioHandlers, onPlaybackActivity?: (active: boolean) => void): Promise<void> {
    this.onPlaybackActivity = onPlaybackActivity || null;
    const Ctx = (window.AudioContext || (window as any).webkitAudioContext);
    this.ctx = new Ctx();
    // iOS: contexts start suspended until resumed from a user gesture; start()
    // is called from the mic button's click handler so this succeeds.
    if (this.ctx.state === 'suspended') await this.ctx.resume();

    const supported = navigator.mediaDevices.getSupportedConstraints?.() || {};
    const audioConstraints: MediaTrackConstraints = {
      channelCount: 1,
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    };
    // Safari exposes voiceIsolation on supported iPhones even though the DOM
    // typings used by CRA 5 do not know the property yet.
    if ((supported as any).voiceIsolation) {
      (audioConstraints as any).voiceIsolation = true;
    }
    this.stream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints });

    this.source = this.ctx.createMediaStreamSource(this.stream);
    this.processor = this.ctx.createScriptProcessor(4096, 1, 1);
    const inputRate = this.ctx.sampleRate;

    this.processor.onaudioprocess = (e) => {
      const input = e.inputBuffer.getChannelData(0);
      // Level for the orb (cheap RMS over a stride).
      if (handlers.onLevel) {
        let sum = 0;
        for (let i = 0; i < input.length; i += 8) sum += input[i] * input[i];
        handlers.onLevel(Math.min(1, Math.sqrt(sum / (input.length / 8)) * 4));
      }
      const pcm16k = resamplePcm16(input, inputRate, 16000);
      const pcm24k = resamplePcm16(input, inputRate, 24000);
      const nowMs = performance.now();
      const aecIsWarmingUp = this.echoGuardStartedAt > 0
        && nowMs - this.echoGuardStartedAt < ECHO_GUARD_WARMUP_MS;
      if (aecIsWarmingUp && (this.playbackActive || nowMs < this.suppressCaptureUntil)) {
        return;
      }
      handlers.onChunk(pcm16ToBase64(pcm16k), pcm16ToBase64(pcm24k));
    };

    this.source.connect(this.processor);
    // ScriptProcessor must be wired to the destination to fire; a zero-gain
    // tap keeps the mic from echoing back out of the speakers.
    const mute = this.ctx.createGain();
    mute.gain.value = 0;
    this.processor.connect(mute);
    mute.connect(this.ctx.destination);
  }

  playChunk(b64Pcm24k: string): void {
    if (!this.ctx) return;
    const bin = atob(b64Pcm24k);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const samples = new Int16Array(bytes.buffer);
    if (!samples.length) return;
    const buf = this.ctx.createBuffer(1, samples.length, 24000);
    const ch = buf.getChannelData(0);
    for (let i = 0; i < samples.length; i++) ch[i] = samples[i] / 32768;

    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.connect(this.ctx.destination);
    const now = this.ctx.currentTime;
    this.nextPlayTime = Math.max(now + 0.02, this.nextPlayTime);
    src.start(this.nextPlayTime);
    this.nextPlayTime += buf.duration;
    this.playing.push(src);
    if (!this.echoGuardStartedAt) this.echoGuardStartedAt = performance.now();
    this.playbackActive = true;
    src.onended = () => {
      this.playing = this.playing.filter((s) => s !== src);
    };

    if (this.onPlaybackActivity) this.onPlaybackActivity(true);
    if (this.activityTimer) clearTimeout(this.activityTimer);
    const remainMs = Math.max(0, (this.nextPlayTime - now) * 1000) + 150;
    this.activityTimer = setTimeout(() => {
      this.playbackActive = false;
      this.suppressCaptureUntil = performance.now() + ECHO_GUARD_TAIL_MS;
      if (this.onPlaybackActivity) this.onPlaybackActivity(false);
    }, remainMs);
  }

  // Barge-in: the user talked over the AI; drop everything queued.
  stopPlayback(): void {
    this.playing.forEach((s) => {
      try { s.stop(); } catch { /* already ended */ }
    });
    this.playing = [];
    this.nextPlayTime = 0;
    this.playbackActive = false;
    this.suppressCaptureUntil = performance.now() + ECHO_GUARD_TAIL_MS;
    if (this.activityTimer) clearTimeout(this.activityTimer);
    if (this.onPlaybackActivity) this.onPlaybackActivity(false);
  }

  stop(): void {
    this.stopPlayback();
    try { this.processor?.disconnect(); } catch { /* noop */ }
    try { this.source?.disconnect(); } catch { /* noop */ }
    this.stream?.getTracks().forEach((t) => t.stop());
    try { this.ctx?.close(); } catch { /* noop */ }
    this.ctx = null;
    this.stream = null;
    this.source = null;
    this.processor = null;
    this.echoGuardStartedAt = 0;
    this.playbackActive = false;
    this.suppressCaptureUntil = 0;
  }
}
